import { CalendarIcon, ChevronRightIcon } from '@chakra-ui/icons';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Divider,
  Flex,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Skeleton,
  SkeletonCircle,
  Stack,
  Text,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import MoreIcon from '../../../assets/icons/more-horizontal-icon.svg';
import Pagination from '../../../components/pagination/Pagination';
import ResourceGrid from '../../../components/resource-grid/ResourceGrid';
import { usePagination } from '../../../hooks/usePagination';
import { usePermissions } from '../../../hooks/usePermissions';
import { useResourceGridActions } from '../../../hooks/useResourceGridActions';
import { getMarketList } from '../../../services/markets/getMarketList';
import { getPlansList } from '../../../services/plans/listPlans';
import { updatePlan } from '../../../services/plans/updatePlan';
import { State } from '../../../types/markets/types';
import {
  InsurancePlan,
  PlansResponse,
  UpdatePlanRequest,
} from '../../../types/plans/types';
import { analytics } from '../../../utils/analytics';
import { useFeatureFlag } from '../../../utils/featureFlags';
import { getStatesForMarketIds } from '../../../utils/getStatesForMarketIds';
import { stateOptions } from '../../../utils/stateOptions';
import InsuranceRowDrawer from './InsuranceRowDrawer';
import { PLAN_DETAIL_COPY } from './planDetailCopy';
import { createStaticColumns } from './planDetailsColumns';

// Not Found Component
const PlanNotFoundCard: React.FC<{
  planId?: string;
  onGoToPlans: () => void;
  onRetry: () => void;
}> = ({ planId, onGoToPlans, onRetry }) => {
  useEffect(() => {
    // Log info metric with plan id param
    if (planId) {
      console.info('Plan not found:', { planId });
    }
  }, [planId]);

  return (
    <Box
      maxW="md"
      mx="auto"
      mt="8"
      p="8"
      bg="white"
      borderRadius="lg"
      boxShadow="md"
      textAlign="center"
    >
      <Text fontSize="xl" fontWeight="bold" mb="4" color="gray.700">
        {PLAN_DETAIL_COPY.notFound.title}
      </Text>
      <Text color="gray.600" mb="6">
        {PLAN_DETAIL_COPY.notFound.description}
      </Text>
      <Stack direction="row" spacing="4" justify="center">
        <Button
          colorScheme="blue"
          onClick={onGoToPlans}
          aria-label="Navigate to plans list"
        >
          {PLAN_DETAIL_COPY.notFound.goToPlansButton}
        </Button>
        <Button variant="outline" onClick={onRetry}>
          {PLAN_DETAIL_COPY.notFound.retryButton}
        </Button>
      </Stack>
    </Box>
  );
};

// Date chip component for consistent date display
const DateChip: React.FC<{
  label: string;
  value: string;
  icon: React.ComponentType<{ boxSize?: number | string }>;
  variant?: 'default' | 'active' | 'expired';
}> = ({ label, value, icon: IconComponent, variant = 'default' }) => {
  const getVariantStyles = () => {
    switch (variant) {
      case 'active':
        return {
          bg: 'green.50',
          color: 'green.700',
          borderColor: 'green.200',
        };
      case 'expired':
        return {
          bg: 'red.50',
          color: 'red.700',
          borderColor: 'red.200',
        };
      default:
        return {
          bg: 'blue.50',
          color: 'blue.700',
          borderColor: 'blue.200',
        };
    }
  };

  const styles = getVariantStyles();

  return (
    <HStack
      spacing={1}
      px={2}
      py={1}
      borderRadius="md"
      border="1px solid"
      bg={styles.bg}
      color={styles.color}
      borderColor={styles.borderColor}
      fontSize="0.75rem"
      fontWeight="500"
    >
      <IconComponent boxSize={3} />
      <Text fontSize="0.75rem" fontWeight="500">
        {label}: {value}
      </Text>
    </HStack>
  );
};

const PlanDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const fetchStartedRef = useRef(false);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    fetchStartedRef.current = false;
  }, [id]);

  // Get user permissions
  const { canEditInsurance, isAdmin, isReady, permissions } =
    usePermissions();

  // Create columns with permission checks
  const columnsWithPermissions = useMemo(
    () => createStaticColumns({}, canEditInsurance, isAdmin),
    [canEditInsurance, isAdmin]
  );

  // Check feature flag for plan details revamp
  const isPlanDetailsRevampEnabled = useFeatureFlag(
    'plan_details_revamp_v0'
  );

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] =
    useState<InsurancePlan | null>(null);
  const [drawerOpenTime, setDrawerOpenTime] = useState<number | null>(
    null
  );
  const [focusedRowIndex, setFocusedRowIndex] = useState<number>(-1);

  // Market/state data is needed to resolve a plan's marketIds to states (see
  // getStatesForMarketIds). Fetched explicitly here rather than relying on
  // whatever another page happened to leave in localStorage - direct
  // navigation to this route never populates that cache otherwise, which
  // silently resolved every plan to zero states.
  const [statesData, setStatesData] = useState<State[]>([]);
  const [statesLoading, setStatesLoading] = useState(true);
  // A failed fetch must not look the same as "loaded, this plan has no
  // states" - without this, statesData simply stays [] and the States
  // section below silently renders nothing, misrepresenting an assigned
  // plan as unassigned.
  const [statesLoadError, setStatesLoadError] = useState(false);

  const loadStatesData = useCallback(() => {
    // Wait for permissions to resolve: the customer token this request
    // needs isn't available yet before isReady, so firing early can fail
    // once with no retry. The admin flag also controls whether external
    // market records come back at all - the same convention PlansList uses.
    if (!isReady) return;
    const adminFlag = permissions.isAdmin;

    setStatesLoading(true);
    setStatesLoadError(false);
    getMarketList(undefined, undefined, undefined, adminFlag)
      .then(({ data }) => {
        if (data?.states) {
          setStatesData(data.states);
        } else {
          setStatesLoadError(true);
        }
      })
      .catch((error) => {
        console.error('Failed to fetch market/state data:', error);
        setStatesLoadError(true);
      })
      .finally(() => setStatesLoading(false));
  }, [isReady, permissions.isAdmin]);

  useEffect(() => {
    loadStatesData();
  }, [loadStatesData]);

  // Tab management - only providers tab

  const filters = useMemo<Record<string, string | undefined>>(
    () => ({ insuranceIds: id }),
    [id]
  );

  const {
    items: plan,
    loading: isLoading,
    currentPage,
    totalPages,
    totalCount,
    handlePageChange,
    refetch,
  } = usePagination<InsurancePlan, PlansResponse>(
    useCallback(
      (startToken) => getPlansList(startToken, filters, 10),
      [filters]
    ),
    10,
    {},
    filters,
    isReady
  );

  // Track a single 'view' event per plan id once the detail data has loaded
  const hasTrackedViewRef = useRef<string | null>(null);
  useEffect(() => {
    if (
      !isLoading &&
      plan &&
      plan.length > 0 &&
      hasTrackedViewRef.current !== id
    ) {
      hasTrackedViewRef.current = id ?? null;
      analytics.trackEvent('view', 'plan');
    }
  }, [isLoading, plan, id]);

  // Delete confirmation modal
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();

  // Resource grid actions for standardized action menu
  const resourceGridContext = {
    scopeType: 'plan' as const,
    scopeId: id || '',
    activeFilters: filters,
    columnsMode: 'plans' as const,
  };
  const resourceGridActions = useResourceGridActions(
    resourceGridContext
  );

  // Determine what data to display
  const displayPlans = useMemo(() => {
    return plan || [];
  }, [plan]);

  const openDrawerForRow = useCallback(
    (row: InsurancePlan) => {
      setSelectedRow(row);
      setIsDrawerOpen(true);
      setDrawerOpenTime(Date.now());

      // Track analytics
      if (id) {
        analytics.trackPlanDetailsRowEditOpened(id, row.provider.id);
      }
    },
    [id]
  );

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedRow(null);
    setDrawerOpenTime(null);
  };

  useEffect(() => {
    const editRowHandler = (e: Event) => {
      const custom = e as CustomEvent<{
        providerId: string;
        practiceId: string;
      }>;
      if (!plan) return;
      const { providerId, practiceId } = custom.detail || {};
      if (!providerId || !practiceId) return;
      const row = plan.find(
        (r) =>
          r.provider.id === providerId && r.practice.id === practiceId
      );
      if (row) openDrawerForRow(row);
    };

    const viewProviderHandler = (e: Event) => {
      const custom = e as CustomEvent<{ providerId: string }>;
      const { providerId } = custom.detail || {};
      if (providerId) {
        navigate(`/providers/${providerId}`);
      }
    };

    const bulkEditHandler = (e: Event) => {
      const custom = e as CustomEvent<{
        providerId: string;
        practiceId: string;
      }>;
      const { providerId, practiceId } = custom.detail || {};
      if (providerId && practiceId && id) {
        // Track analytics
        analytics.trackNavigateToBulkEditor(
          id,
          providerId,
          'planDetails'
        );

        navigate(
          `/plans/${id}/edit-group?providerId=${providerId}&practiceId=${practiceId}&from=planDetails&keepHighlight=true`
        );
      }
    };

    const editProviderHandler = (e: Event) => {
      const custom = e as CustomEvent<{ providerId: string }>;
      const { providerId } = custom.detail || {};
      if (providerId) {
        navigate(`/providers/${providerId}/edit`);
      }
    };

    const editProviderCredentialStatusHandler = (e: Event) => {
      const custom = e as CustomEvent<{
        providerId: string;
        providerName: string;
      }>;
      const { providerId } = custom.detail || {};
      if (providerId) {
        // Navigate to provider detail and pass a flag to open the insurance modal
        navigate(`/providers/${providerId}?openInsuranceModal=true`);
      }
    };

    window.addEventListener(
      'edit-insurance-row',
      editRowHandler as EventListener
    );
    window.addEventListener(
      'view-provider',
      viewProviderHandler as EventListener
    );
    window.addEventListener(
      'go-to-bulk-edit',
      bulkEditHandler as EventListener
    );
    window.addEventListener(
      'edit-provider',
      editProviderHandler as EventListener
    );
    window.addEventListener(
      'edit-provider-credential-status',
      editProviderCredentialStatusHandler as EventListener
    );

    return () => {
      window.removeEventListener(
        'edit-insurance-row',
        editRowHandler as EventListener
      );
      window.removeEventListener(
        'view-provider',
        viewProviderHandler as EventListener
      );
      window.removeEventListener(
        'go-to-bulk-edit',
        bulkEditHandler as EventListener
      );
      window.removeEventListener(
        'edit-provider',
        editProviderHandler as EventListener
      );
      window.removeEventListener(
        'edit-provider-credential-status',
        editProviderCredentialStatusHandler as EventListener
      );
    };
  }, [plan, navigate, id, openDrawerForRow]);

  // Handle retry for not found
  const handleRetry = useCallback(() => {
    if (refetch) {
      refetch();
    }
  }, [refetch]);

  // Handle go to plans
  const handleGoToPlans = useCallback(() => {
    navigate('/plans');
  }, [navigate]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!plan || plan.length === 0) return;

      // Don't interfere with drawer or input interactions
      if (
        isDrawerOpen ||
        (e.target as HTMLElement)?.tagName === 'INPUT' ||
        (e.target as HTMLElement)?.tagName === 'SELECT'
      )
        return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setFocusedRowIndex((prev) =>
            Math.min(prev + 1, plan.length - 1)
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedRowIndex((prev) => Math.max(prev - 1, -1));
          break;
        case 'Enter':
          e.preventDefault();
          if (focusedRowIndex >= 0 && focusedRowIndex < plan.length) {
            openDrawerForRow(plan[focusedRowIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          if (isDrawerOpen) {
            closeDrawer();
          } else {
            setFocusedRowIndex(-1);
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () =>
      document.removeEventListener('keydown', handleKeyDown);
  }, [plan, focusedRowIndex, isDrawerOpen, openDrawerForRow]);

  useEffect(() => {
    if (isLoading) fetchStartedRef.current = true;
  }, [isLoading]);

  // Redirect to stable page if feature flag is disabled
  if (!isPlanDetailsRevampEnabled) {
    // Track blocked route access
    analytics.trackBlockedRoutePlanDetails(window.location.href);

    // Redirect to plans list
    navigate('/plans', { replace: true });
    return null;
  }

  const updateRowValuesInGrid = (updated: {
    credentialstatus: string;
    value: string;
    age: string;
    minimumAge?: number | null;
    maximumAge?: number | null;
  }) => {
    if (!selectedRow || !plan) return;

    analytics.trackEvent('update', 'insurance');

    // Track analytics for successful save
    if (id && drawerOpenTime) {
      const durationMs = Date.now() - drawerOpenTime;
      const originalRow = selectedRow.plans?.[0];
      const changedFields: string[] = [];

      if (
        originalRow?.credentialstatus !== updated.credentialstatus
      ) {
        changedFields.push('credentialstatus');
      }
      if (originalRow?.value !== updated.value) {
        changedFields.push('acceptingPatients');
      }
      if (originalRow?.age !== updated.age) {
        changedFields.push('ageRange');
      }

      analytics.trackPlanDetailsRowEditSaved(
        id,
        selectedRow.provider.id,
        changedFields,
        durationMs
      );
    }

    // Refetch data to update the table - let usePagination handle the state
    if (refetch) {
      refetch();
    }
  };

  const handleDelete = async () => {
    if (!id || !plan || plan.length === 0) return;

    try {
      // Calculate end date as today minus 2 days
      const today = new Date();
      const twoDaysAgo = new Date(today);
      twoDaysAgo.setDate(today.getDate() - 2);
      const endDate = twoDaysAgo.toISOString().split('T')[0]; // Format as YYYY-MM-DD

      const currentPlan = plan[0].plans[0].insurance;
      if (!currentPlan) return;

      // Update the plan with the new end date
      const updatePayload: UpdatePlanRequest = {
        id: currentPlan.id, // Required by backend API
        endDate: endDate,
      };

      await updatePlan(updatePayload);

      toast({
        title: 'Plan group expired',
        description: 'The plan has been marked as expired',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
      onDeleteClose();
      navigate('/plans');
    } catch (error) {
      toast({
        title: "Couldn't expire plan group. Try again.",
        status: 'error',
        duration: 6000,
        isClosable: true,
        position: 'top-right',
      });
    }
  };

  const handleEdit = () => {
    navigate(`/plans/edit/${id}`);
  };

  const isNotFound = fetchStartedRef.current && !isLoading && (!plan || plan.length === 0);
  const hasData = plan && plan.length > 0;
  const currentPlan = hasData ? plan[0] : null;

  return (
    <>
      <Helmet>
        <title>
          {hasData
            ? `Plan Details - ${currentPlan?.plans[0].insurance?.friendlyName}`
            : 'Plan Details'}
        </title>
      </Helmet>
      <Breadcrumb
        mt="2rem"
        ml="2rem"
        mr="2rem"
        mb="1.62rem"
        separator={<ChevronRightIcon color="gray.500" />}
      >
        <BreadcrumbItem
          onClick={() => {
            navigate('/');
          }}
        >
          <BreadcrumbLink>
            <Text
              fontSize="0.875rem"
              fontWeight="400"
              lineHeight="1.25rem"
              color="gray.700"
            >
              Home
            </Text>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem
          onClick={() => {
            navigate('/plans');
          }}
        >
          <BreadcrumbLink>
            <Text
              fontSize="0.875rem"
              fontWeight="400"
              lineHeight="1.25rem"
              color="gray.700"
            >
              Plans
            </Text>
          </BreadcrumbLink>
        </BreadcrumbItem>

        <BreadcrumbItem isCurrentPage>
          <Skeleton isLoaded={isReady && !isLoading}>
            <BreadcrumbLink href="#">
              <Text
                fontSize="0.875rem"
                fontWeight="400"
                lineHeight="1.25rem"
                color="gray.700"
              >
                {hasData
                  ? `${currentPlan?.plans[0].insurance?.friendlyName?.toLocaleUpperCase()}`
                  : isNotFound
                  ? 'Plan Not Found'
                  : 'Loading...'}
              </Text>
            </BreadcrumbLink>
          </Skeleton>
        </BreadcrumbItem>
      </Breadcrumb>

      {!isReady || isLoading ? (
        <>
          {/* Header Section Skeleton - Matching Market Details Layout */}
          <Flex
            m="2.31rem 2rem 3.5rem 2rem"
            gap="1.5rem"
            justifyContent={'center'}
            alignItems={'flex-start'}
          >
            <Flex
              p="1.5rem"
              flexDirection={'column'}
              justifyContent={'center'}
              gap="1.5rem"
              borderRadius={'0.5rem'}
              width="55%"
              background="background.surfaceTeritiary"
            >
              <HStack justify="space-between" align="flex-start">
                <VStack align="start" spacing="0.5rem">
                  <Skeleton height="2.5rem" width="20rem" />
                  <Skeleton height="1.5rem" width="6rem" />
                </VStack>
                <SkeletonCircle size="2.5rem" />
              </HStack>
              <Skeleton height="2rem" width="8rem" />
              <VStack align="start" spacing="0.5rem">
                <Skeleton height="1.25rem" width="12rem" />
                <Skeleton height="1.25rem" width="10rem" />
                <HStack spacing="1rem" mt="0.5rem">
                  <Skeleton height="1.5rem" width="8rem" />
                  <Skeleton height="1.5rem" width="8rem" />
                </HStack>
              </VStack>
            </Flex>
            <Flex flex="1" height="100%" alignItems="center">
              <Box width="100%" height="400px">
                <Skeleton
                  height="100%"
                  width="100%"
                  borderRadius="0.5rem"
                />
              </Box>
            </Flex>
          </Flex>

          {/* Resources Section Skeleton */}
          <Box m="2rem">
            <VStack spacing="1.5rem" align="stretch">
              <Skeleton height="2rem" width="8rem" />
              <Skeleton height="300px" width="100%" />
            </VStack>
          </Box>
        </>
      ) : isNotFound ? (
        <PlanNotFoundCard
          planId={id}
          onGoToPlans={handleGoToPlans}
          onRetry={handleRetry}
        />
      ) : (
        <>
          {/* Header Section - Matching Market Details Layout */}
          <Flex
            m="2.31rem 2rem 3.5rem 2rem"
            gap="1.5rem"
            justifyContent={'center'}
            alignItems={'flex-start'}
          >
            <Flex
              p="1.5rem"
              flexDirection={'column'}
              justifyContent={'center'}
              gap="1.5rem"
              borderRadius={'0.5rem'}
              width="55%"
              background="background.surfaceTeritiary"
            >
              <HStack justify="space-between" align="flex-start">
                <VStack align="start" spacing="0.5rem">
                  <Text
                    color="text.primary"
                    fontSize="1.875rem"
                    fontWeight={700}
                    lineHeight={'133%'}
                  >
                    {plan && plan?.length > 0
                      ? plan[0].plans[0].insurance?.friendlyName
                      : 'Loading...'}
                  </Text>
                  <HStack spacing="0.5rem">
                    <Badge
                      colorScheme={
                        plan &&
                        plan?.length > 0 &&
                        plan[0].plans[0].insurance?.endDate &&
                        new Date(plan[0].plans[0].insurance.endDate) <
                          new Date()
                          ? 'red'
                          : 'green'
                      }
                      variant="solid"
                      fontSize="0.75rem"
                      px="0.75rem"
                      py="0.25rem"
                      borderRadius="0.375rem"
                    >
                      {plan &&
                      plan?.length > 0 &&
                      plan[0].plans[0].insurance?.endDate &&
                      new Date(plan[0].plans[0].insurance.endDate) <
                        new Date()
                        ? 'Expired'
                        : 'Active'}
                    </Badge>
                  </HStack>
                </VStack>

                {(canEditInsurance() || isAdmin()) && id && (
                  <Menu>
                    <MenuButton
                      as={IconButton}
                      icon={<img src={MoreIcon} />}
                      size="sm"
                      aria-label="Options"
                      borderRadius="0.375rem"
                      background={'background.secondary'}
                      width="2.5rem"
                      height="2.5rem"
                      cursor={'pointer'}
                      border="none"
                      transition="all 0.2s ease-in-out"
                    />
                    <MenuList>
                      <MenuItem onClick={handleEdit}>
                        Edit Plan Details
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          if (id && plan && plan.length > 0) {
                            const planFriendlyName = plan[0].plans[0].insurance?.friendlyName || '';
                            // Track analytics
                            analytics.trackNavigateToBulkEditor(
                              id,
                              '',
                              'planDetails'
                            );
                            // Navigate to bulk edit with plan filter and plan name
                            const planNameParam = planFriendlyName ? `&planName=${encodeURIComponent(planFriendlyName)}` : '';
                            navigate(
                              `/plans/bulk-edit?from=planDetails&filter_insuranceIds=${id}${planNameParam}`
                            );
                          }
                        }}
                      >
                        Bulk Edit Insurance Rows
                      </MenuItem>
                      <MenuItem onClick={onDeleteOpen}>
                        Delete Insurance Plan
                      </MenuItem>
                    </MenuList>
                  </Menu>
                )}
              </HStack>
              <Text
                color="text.secondary"
                fontSize="1.5rem"
                fontWeight={700}
                lineHeight={'133%'}
              >
                Plan Details
              </Text>

              {plan && plan?.length > 0 && (
                <VStack align="start" spacing="0.5rem">
                  <Text
                    fontSize="0.875rem"
                    color="text.secondary"
                    fontWeight="400"
                  >
                    Insurance Plan Group
                  </Text>
                  <HStack spacing="1rem" mt="0.5rem">
                    {plan[0].plans[0].insurance?.startDate && (
                      <DateChip
                        label="Effective"
                        value={new Date(
                          plan[0].plans[0].insurance.startDate
                        ).toLocaleDateString()}
                        icon={CalendarIcon}
                        variant="active"
                      />
                    )}
                    {plan[0].plans[0].insurance?.endDate && (
                      <DateChip
                        label="Expires"
                        value={new Date(
                          plan[0].plans[0].insurance.endDate
                        ).toLocaleDateString()}
                        icon={CalendarIcon}
                        variant={
                          new Date(
                            plan[0].plans[0].insurance.endDate
                          ) > new Date()
                            ? 'default'
                            : 'expired'
                        }
                      />
                    )}
                  </HStack>
                  {/* Display States */}
                  {(() => {
                    if (statesLoading) return null;

                    if (statesLoadError) {
                      return (
                        <HStack spacing="0.5rem" mt="0.5rem">
                          <Text fontSize="0.8125rem" color="red.600">
                            Could not load this plan's states.
                          </Text>
                          <Button
                            size="xs"
                            variant="link"
                            colorScheme="red"
                            onClick={loadStatesData}
                          >
                            Retry
                          </Button>
                        </HStack>
                      );
                    }

                    const states = getStatesForMarketIds(
                      plan[0].plans[0].insurance?.marketIds,
                      statesData
                    );
                    if (states.length === 0) return null;
                    return (
                      <VStack align="start" spacing="0.5rem" mt="0.5rem">
                        <Text
                          fontSize="0.875rem"
                          color="text.secondary"
                          fontWeight="400"
                        >
                          States
                        </Text>
                        <HStack spacing="0.5rem" flexWrap="wrap">
                          {states.map((abbreviation) => {
                            const option = stateOptions.find(
                              (o) => o.value === abbreviation
                            );
                            return (
                              <Badge
                                key={abbreviation}
                                colorScheme="blue"
                                variant="outline"
                                fontSize="0.75rem"
                                px="0.75rem"
                                py="0.25rem"
                                borderRadius="0.375rem"
                              >
                                {option
                                  ? `${option.label} (${option.value})`
                                  : abbreviation}
                              </Badge>
                            );
                          })}
                        </HStack>
                      </VStack>
                    );
                  })()}
                </VStack>
              )}
            </Flex>
            <Flex flex="1" height="100%" alignItems="center">
              <Box width="100%" height="400px">
                {plan && plan?.length > 0 && (
                  <Box
                    p="1.5rem"
                    bg="white"
                    borderRadius="0.75rem"
                    border="1px solid"
                    borderColor="gray.200"
                    boxShadow="sm"
                    height="100%"
                    overflowY="auto"
                  >
                    <Text
                      fontSize="1.125rem"
                      fontWeight="600"
                      color="text.primary"
                      mb="1rem"
                    >
                      Plan Attributes
                    </Text>
                    <VStack spacing="0.75rem" align="stretch">
                      <Flex justify="space-between" align="center">
                        <Text fontSize="0.875rem" fontWeight="500">
                          Primary Plan
                        </Text>
                        <Badge
                          colorScheme={
                            plan[0].plans[0].insurance?.primaryPlan
                              ? 'green'
                              : 'red'
                          }
                          variant="solid"
                          fontSize="0.75rem"
                          px="0.75rem"
                          py="0.25rem"
                          borderRadius="full"
                        >
                          {plan[0].plans[0].insurance?.primaryPlan
                            ? 'Yes'
                            : 'No'}
                        </Badge>
                      </Flex>
                      <Flex justify="space-between" align="center">
                        <Text fontSize="0.875rem" fontWeight="500">
                          Secondary Plan
                        </Text>
                        <Badge
                          colorScheme={
                            plan[0].plans[0].insurance?.secondaryPlan
                              ? 'green'
                              : 'red'
                          }
                          variant="solid"
                          fontSize="0.75rem"
                          px="0.75rem"
                          py="0.25rem"
                          borderRadius="full"
                        >
                          {plan[0].plans[0].insurance?.secondaryPlan
                            ? 'Yes'
                            : 'No'}
                        </Badge>
                      </Flex>
                      <Flex justify="space-between" align="center">
                        <Text fontSize="0.875rem" fontWeight="500">
                          Commercial
                        </Text>
                        <Badge
                          colorScheme={
                            plan[0].plans[0].insurance?.commercial
                              ? 'green'
                              : 'red'
                          }
                          variant="solid"
                          fontSize="0.75rem"
                          px="0.75rem"
                          py="0.25rem"
                          borderRadius="full"
                        >
                          {plan[0].plans[0].insurance?.commercial
                            ? 'Yes'
                            : 'No'}
                        </Badge>
                      </Flex>
                      <Flex justify="space-between" align="center">
                        <Text fontSize="0.875rem" fontWeight="500">
                          HMO
                        </Text>
                        <Badge
                          colorScheme={
                            plan[0].plans[0].insurance?.hmo
                              ? 'green'
                              : 'red'
                          }
                          variant="solid"
                          fontSize="0.75rem"
                          px="0.75rem"
                          py="0.25rem"
                          borderRadius="full"
                        >
                          {plan[0].plans[0].insurance?.hmo
                            ? 'Yes'
                            : 'No'}
                        </Badge>
                      </Flex>
                      <Flex justify="space-between" align="center">
                        <Text fontSize="0.875rem" fontWeight="500">
                          Medicare Advantage
                        </Text>
                        <Badge
                          colorScheme={
                            plan[0].plans[0].insurance
                              ?.medicareAdvantage
                              ? 'green'
                              : 'red'
                          }
                          variant="solid"
                          fontSize="0.75rem"
                          px="0.75rem"
                          py="0.25rem"
                          borderRadius="full"
                        >
                          {plan[0].plans[0].insurance
                            ?.medicareAdvantage
                            ? 'Yes'
                            : 'No'}
                        </Badge>
                      </Flex>
                      <Flex justify="space-between" align="center">
                        <Text fontSize="0.875rem" fontWeight="500">
                          Marketplace
                        </Text>
                        <Badge
                          colorScheme={
                            plan[0].plans[0].insurance?.marketplace
                              ? 'green'
                              : 'red'
                          }
                          variant="solid"
                          fontSize="0.75rem"
                          px="0.75rem"
                          py="0.25rem"
                          borderRadius="full"
                        >
                          {plan[0].plans[0].insurance?.marketplace
                            ? 'Yes'
                            : 'No'}
                        </Badge>
                      </Flex>
                      <Flex justify="space-between" align="center">
                        <Text fontSize="0.875rem" fontWeight="500">
                          VA
                        </Text>
                        <Badge
                          colorScheme={
                            plan[0].plans[0].insurance?.va
                              ? 'green'
                              : 'red'
                          }
                          variant="solid"
                          fontSize="0.75rem"
                          px="0.75rem"
                          py="0.25rem"
                          borderRadius="full"
                        >
                          {plan[0].plans[0].insurance?.va
                            ? 'Yes'
                            : 'No'}
                        </Badge>
                      </Flex>
                    </VStack>
                  </Box>
                )}
              </Box>
            </Flex>
          </Flex>

          {/* Resources Section - Matching Market Details Pattern */}
          <Box ml="2rem" mr="2rem">
            <Flex
              justifyContent="space-between"
              alignItems="center"
              mb="1rem"
            >
              <Flex gap="1rem">
                <Button
                  height={'1.75rem'}
                  padding={'0.25rem 1rem'}
                  borderRadius={'624.9375rem'}
                  background="background.blue100"
                  color="text.blue700"
                  fontSize={'0.875rem'}
                  fontWeight={600}
                  lineHeight={'1.25rem'}
                >
                  Providers {totalCount}
                </Button>
              </Flex>
            </Flex>
            <Divider mb="1rem" />
            <Box>
              <Stack gap="1rem">
                {plan && plan?.length > 0 && (
                  <>
                    <ResourceGrid<InsurancePlan>
                      data={displayPlans}
                      columns={columnsWithPermissions}
                      actionMenuContext={resourceGridContext}
                      actionMenuActions={resourceGridActions}
                      planId={id}
                    />

                    {/* Standard pagination like other pages */}
                    {totalPages > 1 && (
                      <Flex justify="center" mt="1rem">
                        <Pagination
                          currentPage={currentPage}
                          totalPages={totalPages}
                          onPageChange={(page) => {
                            handlePageChange(page);
                            // Track analytics
                            if (id) {
                              analytics.trackPaginationChanged(
                                id,
                                page,
                                10
                              );
                            }
                          }}
                        />
                      </Flex>
                    )}
                  </>
                )}

                {!isLoading && displayPlans.length === 0 && (
                  <Box textAlign="center" py="4rem">
                    <Text color="gray.500" fontSize="1rem">
                      No providers assigned to this plan yet.
                    </Text>
                  </Box>
                )}
              </Stack>
            </Box>
          </Box>
        </>
      )}
      <InsuranceRowDrawer
        isOpen={isDrawerOpen}
        onClose={closeDrawer}
        planId={id || ''}
        row={selectedRow}
        onSaved={updateRowValuesInGrid}
      />

      {/* Delete Confirmation Modal */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
        size="lg"
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader
              fontSize="lg"
              fontWeight="bold"
              color="red.600"
            >
              Delete Plan Group
            </AlertDialogHeader>

            <AlertDialogBody>
              <VStack align="start" spacing="1rem">
                <Text>
                  Are you sure you want to delete the plan group "
                  <Text as="span" fontWeight="600">
                    {plan && plan?.length > 0
                      ? plan[0].plans[0].insurance?.friendlyName
                      : 'Unknown Plan'}
                  </Text>
                  "?
                </Text>

                <Box
                  p="1rem"
                  bg="red.50"
                  borderRadius="md"
                  border="1px solid"
                  borderColor="red.200"
                  width="100%"
                >
                  <Text
                    fontSize="sm"
                    color="red.700"
                    fontWeight="500"
                    mb="0.5rem"
                  >
                    What will happen:
                  </Text>
                  <VStack
                    align="start"
                    spacing="0.25rem"
                    fontSize="sm"
                    color="red.600"
                  >
                    <Text>• The plan will be marked as expired</Text>
                    <Text>
                      • All providers will no longer be able to use
                      this plan
                    </Text>
                    <Text>• This action cannot be undone</Text>
                  </VStack>
                </Box>
              </VStack>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleDelete} ml={3}>
                Delete Plan Group
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default PlanDetails;
